import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, isNull } from 'lodash'

import Modal from '@ublique/components/LoadingModal'
import Checkbox from '@ublique/components/Checkbox'
import TextInput from '@ublique/components/TextInput'
import { Star24, StarFilled24 } from '@carbon/icons-react'

import { createFilterbarApiActions, saveFilterHandler } from './actions'
import './index.scss'

const FORBIDDEN_KEYS = ['none', 'nessuno', 'default']

const ModalSaveFilter = ({ open, close, data: { data, outputName } }) => {
  const [title, setTitle] = useState('')
  const [isError, setIsError] = useState(false)
  const [filterList, setFilterList] = useState(undefined)
  const [overwrite, setOverwrite] = useState(false)
  const [favorite, setFavorite] = useState(false)

  const { t } = useTranslation('Common')
  const { getFilterList } = createFilterbarApiActions(outputName)

  useEffect(() => {
    getFilterList()
      .then((list) => setFilterList(list))
      .catch(() => setFilterList(null))
  }, [])

  const onChange = useCallback(({ target: { value } }) => {
    setTitle(value)
    setIsError(
      isEmpty(value) ||
        value.length > 32 ||
        FORBIDDEN_KEYS.includes(value.toLowerCase())
    )
  }, [])

  const onSubmit = useCallback(async () => {
    let body = {
      name: title,
      favorite,
      output_name: outputName,
      sections: data
    }

    if (overwrite) {
      const { id, favorite } = filterList.find(
        (filter) => filter.name === title
      )
      body = { ...body, id, favorite }
    }

    await saveFilterHandler(body, outputName, t)
    close()
  }, [t, title, overwrite, favorite, data, outputName])

  const titleAlreadyExisting = useMemo(() => {
    if (!isEmpty(filterList) && !FORBIDDEN_KEYS.includes(title.toLowerCase())) {
      const matching = filterList.find((filter) => filter.name === title)

      if (!matching) setOverwrite(false)

      return matching
    }

    return null
  }, [filterList, title])

  const StarFavorite = useMemo(() => {
    const onClick = () => setFavorite((fav) => !fav)
    const [Component, className] = favorite
      ? [StarFilled24, 'star-filled']
      : [Star24, 'star-empty']

    return <Component className={className} onClick={onClick} />
  }, [favorite])

  const primaryButtonDisabled =
    isEmpty(title.trim()) ||
    isError ||
    isNull(filterList) ||
    (titleAlreadyExisting && !overwrite)

  return (
    <Modal
      open={open}
      className='filter-modal'
      size='sm'
      iconDescription={t('close')}
      modalHeading={t('SAVE_NEW_FILTER')}
      onRequestClose={close}
      onRequestSubmit={onSubmit}
      onSecondarySubmit={close}
      primaryButtonText={t('Save')}
      confirmLoadingMessage={t('Save')}
      secondaryButtonText={t('Cancel')}
      primaryButtonDisabled={primaryButtonDisabled}
      preventCloseOnClickOutside
    >
      <div className='save-form'>
        <TextInput
          value={title}
          onChange={onChange}
          labelText={t('FILTER_NAME')}
          invalid={isError}
          disabled={isNull(filterList)}
          invalidText={t('FILTER_NAME_ERROR')}
        />
        {StarFavorite}
      </div>
      {titleAlreadyExisting && (
        <Checkbox
          id='filter-save-overwrite'
          checked={overwrite}
          labelText={t('FILTER_OVERWRITE')}
          onChange={(e) => setOverwrite(e)}
        />
      )}
      {isNull(filterList) && (
        <p className='fetching-error'>{t('MODAL_FILTER_FETCHING_ERROR')}</p>
      )}
    </Modal>
  )
}

export default React.memo(ModalSaveFilter)
